import { useState } from "owa-react";
import React from "react";
import { KeytipData } from "@fluentui/react/lib/KeytipData";
import { makeStyles, mergeClasses, tokens, Button, Menu, MenuTrigger, MenuPopover, type MenuOpenChangeData, type MenuOpenEvent, } from "@fluentui/react-components";
import { getRibbonTopBarStylingConstants } from "owa-acui";
import { RIBBON_TAB_FONT_WEIGHT, RIBBON_TAB_HORIZONTAL_PADDING, RIBBON_TAB_LINE_HEIGHT, RIBBON_TAB_MIN_HEIGHT } from "owa-command-ribbon";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { observer } from "owa-mobx-react";
import { FileButton as fileButtonStrings_FileButton } from "./fileButton.locstring.json";
import loc from "owa-localize";

interface FileButtonProps {
    menuContent: React.ReactNode;
    label?: string;
    onClick?: () => void;
}

const btnExecute = (executeTarget: HTMLElement | null, _target: HTMLElement | null) => {
    executeTarget?.click();
};

const keytipProps = {
    keySequences: ["f"],
    content: "F",
    onExecute: btnExecute,
    hasMenu: true,
    hasDynamicChildren: true,
};

export const FileButton: React.FunctionComponent<FileButtonProps> = observer(({ menuContent, label, onClick }) => {
    const styles = useStyles();
    const isOverlayEnabled = isWin32StylingEnabled();
    const fileLabel = label || loc(fileButtonStrings_FileButton);
    const [open, setOpen] = useState(false);
    const fileButtonHeight = `${getRibbonTopBarStylingConstants().tabHeaderHeight}px`;
    /* oxlint-disable-next-line react-perf/jsx-no-new-object-as-prop -- (https://aka.ms/OWALintWiki)
     * Fix this by memoizing or hoisting the object so its identity remains stable across renders.
     * > JSX attribute values should not contain objects created in the same scope. */
    const fileButtonStyle = isOverlayEnabled
        ? { height: fileButtonHeight, minHeight: fileButtonHeight }
        : undefined;
    
    const handleOpenChange = React.useCallback((event: MenuOpenEvent, data: MenuOpenChangeData) => {
        // If we're closing the menu, we want to stop propagation to prevent the event from bubbling to the compose handler
        // which would close the compose window if it was open.
        if (open && !data.open) {
            event.stopPropagation();
        }
        
        setOpen(data.open);
    }, [open]);
    
    const handleMenuKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape" &&
            event.defaultPrevented &&
            event.currentTarget.contains(event.target as Node)) {
            setOpen(false);
        }
    }, []);
    
    const buttonElementFn = (props: {}) => {
        const triggerProps = props as React.ComponentProps<typeof Button>;
        const mergedClassName = mergeClasses(triggerProps.className, isOverlayEnabled ? styles.buttonOverlay : styles.buttonBase, open && styles.open);
        
        return (<MenuTrigger>
                    <Button appearance="transparent" {...triggerProps} className={mergedClassName} onClick={onClick} style={fileButtonStyle}>
                        {fileLabel}
                    </Button>
                </MenuTrigger>);
    };
    
    return (<Menu open={open} onOpenChange={handleOpenChange}>
                <KeytipData keytipProps={keytipProps}>{buttonElementFn}</KeytipData>

                <MenuPopover onKeyDown={handleMenuKeyDown}>{menuContent}</MenuPopover>
            </Menu>);
}, "FileButton");

const useStyles = makeStyles({
    buttonBase: {
        fontWeight: tokens.fontWeightRegular,
        height: "31px",
        lineHeight: "29px",
        marginLeft: "3px",
        minWidth: "auto",
        padding: `0 10px 0 ${tokens.spacingHorizontalMNudge}`,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            color: tokens.colorNeutralForeground1,
        },
    },
    buttonOverlay: {
        background: "none",
        border: "none",
        borderRadius: tokens.borderRadiusMedium,
        boxSizing: "border-box",
        color: tokens.colorNeutralForeground2,
        fontSize: "14px",
        fontWeight: RIBBON_TAB_FONT_WEIGHT,
        minHeight: RIBBON_TAB_MIN_HEIGHT,
        minWidth: "auto",
        lineHeight: RIBBON_TAB_LINE_HEIGHT,
        marginInlineStart: "0",
        marginInlineEnd: "4px",
        padding: `0 ${RIBBON_TAB_HORIZONTAL_PADDING}`,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            color: tokens.colorNeutralForeground1,
        },
    },
    open: {
        backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
});
